import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './landing_page.css';

/*
  Enterprise-grade Landing Page
  - Modular components defined in this file for simplicity
  - All animations use CSS + IntersectionObserver
*/

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((ent) => {
        if (ent.isIntersecting) ent.target.classList.add('is-revealed');
      });
    }, { threshold: 0.12 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

function Header() {
  return (
    <header className="lp-header">
      <div className="lp-container lp-header-inner">
        <div className="lp-brand">
          <div className="lp-logo brand-heavy">Cortex</div>
        </div>
        <nav className="lp-nav">
          <a href="#features">Features</a>
          <a href="#preview">Preview</a>
          <a href="#faq">FAQ</a>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="lp-hero" id="home">
      <div className="lp-hero-bg" aria-hidden="true">
        <div className="grad-orb orb-a" />
        <div className="grad-orb orb-b" />
        <div className="anim-grad" />
      </div>

      <div className="lp-container lp-hero-inner" data-reveal>
        <div className="lp-hero-copy">
          <h1 className="lp-hero-title">Clarity for teams. Velocity for work.</h1>
          <p className="lp-hero-sub">AI-powered task generation, agent-driven automation, and a modular backend built for teams.</p>
          <div className="lp-hero-ctas">
            <Link to="/signup" className="lp-btn lp-btn--primary">Get started — Free</Link>
            <Link to="/login" className="lp-btn lp-btn--ghost">Sign in</Link>
          </div>
          <div className="lp-hero-trust" aria-hidden>
            <span>Trusted by early teams • SOC-ready architecture • Easy SSO</span>
          </div>
        </div>

        <div className="lp-hero-panel" data-reveal>
          <div className="mock-panel">
            <div className="mock-top">
              <div className="mock-title">Tasks • Today</div>
              <div className="mock-stats">
                <div className="s">34</div>
                <div className="s">6 overdue</div>
              </div>
            </div>
            <div className="mock-chart" aria-hidden>
              <svg viewBox="0 0 200 50" preserveAspectRatio="none">
                <polyline className="chart-line" points="0,40 30,30 60,10 90,18 120,6 150,14 180,8 200,2" />
              </svg>
            </div>
            <div className="mock-chat">
              <div className="chat-msg from">Create a release checklist for v1.4</div>
              <div className="chat-msg to">Done — created 6 subtasks, assigned priorities.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Trust() {
  return (
    <section className="lp-trust" data-reveal>
      <div className="lp-container">
        <div className="trust-logos" aria-hidden>
          <div className="logo mock">Acme</div>
          <div className="logo mock">Stark</div>
          <div className="logo mock">Frost</div>
          <div className="logo mock">Atlas</div>
          <div className="logo mock">Nimbus</div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const ref = useRef();
  const [active, setActive] = useState(false);
  const [tasks, setTasks] = useState(0);
  const [uptime, setUptime] = useState(0);
  const [sats, setSats] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) setActive(true);
      });
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    let a = 0, b = 0, c = 0;
    const t = setInterval(() => {
      if (a < 1_240_000) a += Math.ceil(1_240_000 / 60);
      if (b < 99.99) b = Math.min(99.99, +(b + 0.8).toFixed(2));
      if (c < 4320) c += Math.ceil(4320 / 60);
      setTasks(a);
      setUptime(b);
      setSats(c);
      if (a >= 1_240_000 && b >= 99.99 && c >= 4320) clearInterval(t);
    }, 45);
    return () => clearInterval(t);
  }, [active]);

  return (
    <section className="lp-stats" data-reveal>
      <div className="lp-container lp-stats-grid" ref={ref}>
        <div className="stat">
          <div className="stat-value">{tasks >= 1000 ? `${(tasks/1000).toFixed(1)}k` : tasks}</div>
          <div className="stat-label">Tasks managed</div>
        </div>
        <div className="stat">
          <div className="stat-value">{uptime}%</div>
          <div className="stat-label">Average uptime</div>
        </div>
        <div className="stat">
          <div className="stat-value">{sats >= 1000 ? `${(sats/1000).toFixed(1)}k` : sats}</div>
          <div className="stat-label">Active users</div>
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ title, bullets, icon }) {
  return (
    <div className="feature-card" data-reveal>
      <div className="feature-icon">{icon}</div>
      <div className="feature-body">
        <h3>{title}</h3>
        <ul>
          {bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      </div>
    </div>
  );
}

function Features() {
  const features = [
    {
      title: 'AI Task Generator',
      icon: '🤖',
      bullets: ['Text or image input', 'Auto subtasks & priorities', 'Exportable templates']
    },
    {
      title: 'Agent Chat',
      icon: '💬',
      bullets: ['Multi-session context', 'Actionable responses', 'Session migration']
    },
    {
      title: 'Memory & Knowledge',
      icon: '🧠',
      bullets: ['Short & long term memory', 'Searchable facts', 'Fine-grained controls']
    },
    {
      title: 'Secure Auth',
      icon: '🔐',
      bullets: ['JWT + OTP + Google OAuth', 'SSO & enterprise policies', 'Session encryption']
    },
    {
      title: 'Analytics & Insights',
      icon: '📊',
      bullets: ['Custom dashboards', 'Alerting & reports', 'Exportable CSV']
    },
    {
      title: 'Modular Backend',
      icon: '⚙️',
      bullets: ['Agent, Memory, LLM services', 'Pluggable integrations', 'Scalable deployments']
    }
  ];

  return (
    <section className="lp-features" id="features">
      <div className="lp-container">
        <h2 className="section-title" data-reveal>Platform features</h2>
        <div className="features-grid">
          {features.map((f, i) => <FeatureCard key={i} {...f} />)}
        </div>
      </div>
    </section>
  );
}

function Preview() {
  return (
    <section className="lp-preview" id="preview">
      <div className="lp-container">
        <h2 className="section-title" data-reveal>Interactive product preview</h2>
        <div className="preview-grid">
          <div className="preview-dashboard" data-reveal>
            <div className="dash-header">Smart Dashboard</div>
            <div className="dash-body">
              <div className="mini-chart" aria-hidden>
                <svg viewBox="0 0 200 60" preserveAspectRatio="none"><polyline className="chart-line" points="0,50 30,34 60,20 90,28 120,12 150,22 180,10 200,4" /></svg>
              </div>
              <div className="dash-tasks">
                <div className="task">Write release notes <span className="tag">AI</span></div>
                <div className="task done">Ship v1.3 <span className="tag">Done</span></div>
              </div>
            </div>
          </div>

          <div className="preview-ai" data-reveal>
            <div className="ai-header">Agent chat</div>
            <div className="ai-body">
              <div className="ai-chat">
                <div className="ai-msg from">Plan next sprint focusing on onboarding</div>
                <div className="ai-msg to">Created 5 tasks and assigned owners.</div>
              </div>
              <div className="ai-gen">
                <label>AI Task Generator</label>
                <div className="gen-box">Type a prompt — e.g. "Prepare Q2 launch checklist"</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Architecture() {
  return (
    <section className="lp-architecture" data-reveal>
      <div className="lp-container">
        <h2 className="section-title">AI architecture</h2>
        <div className="arch-grid">
          <div className="arch-node">User
            <div className="arch-line" />
          </div>
          <div className="arch-node">Agent
            <div className="arch-line" />
          </div>
          <div className="arch-node">Memory
            <div className="arch-line" />
          </div>
          <div className="arch-node">LLM
            <div className="arch-line" />
          </div>
          <div className="arch-node">Response</div>
        </div>
      </div>
    </section>
  );
}


function Testimonials() {
  const items = [
    { quote: 'Cortex streamlined our backlog and reduced context switching.', name: 'Aria — Eng. Manager' },
    { quote: 'AI suggestions are shockingly useful — saved us days.', name: 'Sam — PM' },
    { quote: 'Reliable and predictable infra for our enterprise.', name: 'Ravi — CTO' }
  ];
  return (
    <section className="lp-testimonials" data-reveal>
      <div className="lp-container">
        <h2 className="section-title">What teams say</h2>
        <div className="test-grid">
          {items.map((t, i) => (
            <div key={i} className="test-card" data-reveal>
              <blockquote>“{t.quote}”</blockquote>
              <div className="test-by">{t.name}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const faqs = [
    { q: 'How secure is Cortex?', a: 'We support JWT, OTP, Google OAuth, and enterprise SSO integrations. Data is encrypted at rest and in transit.' },
    { q: 'Can I create custom agents?', a: 'Yes — you can create and configure custom agents with triggers, rules, and integrations. Agents can automate task creation, run scheduled jobs, and call external services via webhooks.' },
    { q: 'How does Memory & Knowledge work?', a: 'Cortex supports short-term and long-term memory: short-term keeps recent session context, long-term stores searchable facts and documents. Memories can be tagged, recalled by agents, and used to enrich task generation and suggestions.' }
  ];

  return (
    <section className="lp-faq" id="faq">
      <div className="lp-container">
        <h2 className="section-title">FAQ</h2>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <details key={i} className={`faq-item`} data-reveal>
              <summary className="faq-q">{f.q}</summary>
              <div className="faq-a"><p>{f.a}</p></div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="lp-final" data-reveal>
      <div className="lp-container">
        <div className="final-card">
          <h2>Ready to remove friction from your team's work?</h2>
          <p>Start a free trial today — no credit card required.</p>
          
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="lp-footer">
      <div className="lp-container lp-footer-grid">
        <div>© {new Date().getFullYear()} Cortex — Built with care</div>
      </div>
    </footer>
  );
}

export default function Landing() {
  useReveal();

  useEffect(() => {
    const rootEl = document.getElementById('root');
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevRootOverflow = rootEl ? rootEl.style.overflow : '';
    const prevRootHeight = rootEl ? rootEl.style.height : '';
    // Ensure page can scroll while landing is mounted
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    if (rootEl) {
      rootEl.style.overflow = 'auto';
      rootEl.style.height = 'auto';
      rootEl.style.minHeight = '100%';
    }
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow || '';
      document.body.style.overflow = prevBodyOverflow || '';
      if (rootEl) {
        rootEl.style.overflow = prevRootOverflow || '';
        rootEl.style.height = prevRootHeight || '';
        rootEl.style.minHeight = '';
      }
    };
  }, []);

  return (
    <main className="lp-root">
      <Header />
      <Hero />
      <Trust />
      <Stats />
      <Features />
      <Preview />
      <Architecture />
      <Testimonials />
      <Faq />
      <FinalCta />
      <Footer />
    </main>
  );
}

